import type { ReactNode } from "react";
import storeFacade from "../../../assets/store-facade-render.jpg";
import { STORE } from "../../../data/storeData";

function InfoRow({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 py-2 border-b border-background-200 last:border-b-0">
      <i className={`${icon} text-primary-600 text-base mt-0.5 w-5 shrink-0`} aria-hidden="true"></i>
      <p className="text-xs md:text-sm text-foreground-700 w-20 md:w-24 shrink-0 font-bold">
        {label}
      </p>
      <div className="text-xs md:text-sm text-foreground-900 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function Unconfirmed() {
  return <span className="text-foreground-500">要確認</span>;
}

export default function StoreInfo() {
  return (
    <section id="store" className="relative py-20 md:py-24 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-3">
            <span className="h-px w-8 bg-primary-500" />
            <span className="font-label tracking-widest text-primary-600 text-sm">
              STORE INFO
            </span>
            <span className="h-px w-8 bg-primary-500" />
          </div>
          <h2 className="font-heading font-black text-3xl md:text-4xl text-foreground-950">
            <span className="text-primary-600">店舗情報</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Left card */}
          <div className="bg-background-100 rounded-2xl p-6 md:p-10 border border-background-200 relative overflow-hidden">
            <i className="ri-anchor-line absolute -right-4 -bottom-4 text-9xl text-accent-200" aria-hidden="true" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-primary-600 text-background-50 dark:text-foreground-950 px-3 py-1 rounded-md font-heading font-bold text-xs mb-4">
                <span className="w-2 h-2 rounded-full bg-accent-400" aria-hidden="true" />
                {STORE.statusLabel}
              </div>
              <p
                className="font-heading font-black text-primary-600 text-2xl md:text-3xl leading-snug"
                style={{ WebkitTextStroke: '0.4px currentColor' }}
              >
                {STORE.facilityName}で
                <br />
                営業中
              </p>
              <p className="font-heading font-bold text-primary-700 text-base md:text-lg mt-2">
                {STORE.hoursLabel}（{STORE.lastOrderLabel}）
              </p>

              {/* 住所・Googleマップ導線（最優先） */}
              <div className="mt-6 bg-background-50 rounded-xl p-4 border-2 border-primary-600">
                <div className="flex items-start gap-2">
                  <i className="ri-map-pin-2-fill text-primary-600 text-lg mt-0.5" aria-hidden="true"></i>
                  <div>
                    <p className="font-heading font-bold text-foreground-950">
                      {STORE.facilityName}
                    </p>
                    <p className="text-sm text-foreground-700">{STORE.floorLabel}</p>
                    <p className="text-xs text-foreground-600 mt-1">
                      {STORE.postalCode} {STORE.address}
                    </p>
                  </div>
                </div>
                <a
                  href={STORE.googleMapsSearchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 h-11 min-h-[44px] rounded-full bg-primary-600 hover:bg-primary-700 text-background-50 font-heading font-bold text-sm cursor-pointer transition"
                >
                  <i className="ri-map-2-line" aria-hidden="true"></i>
                  Googleマップで見る
                </a>
              </div>

              {/* 詳細情報 */}
              <div className="mt-6">
                <InfoRow icon="ri-time-line" label="営業時間">
                  {STORE.hoursLabel}（{STORE.lastOrderLabel}）
                </InfoRow>
                <InfoRow icon="ri-calendar-close-line" label="定休日">
                  {STORE.regularHoliday ?? <Unconfirmed />}
                </InfoRow>
                <InfoRow icon="ri-phone-line" label="電話番号">
                  {STORE.phone ? (
                    <a href={`tel:${STORE.phone}`} className="text-primary-700 font-bold cursor-pointer">
                      {STORE.phoneDisplay}
                    </a>
                  ) : (
                    <Unconfirmed />
                  )}
                </InfoRow>
                <InfoRow icon="ri-bank-card-line" label="支払方法">
                  {STORE.paymentMethods ? STORE.paymentMethods.join("・") : <Unconfirmed />}
                </InfoRow>
                <InfoRow icon="ri-takeaway-line" label="テイクアウト">
                  {STORE.takeout ? STORE.takeoutLabel : <Unconfirmed />}
                </InfoRow>
                <InfoRow icon="ri-parent-line" label="お子様連れ">
                  {STORE.kidsStrollerInfo ?? <Unconfirmed />}
                </InfoRow>
                <InfoRow icon="ri-parking-box-line" label="駐車場">
                  {STORE.parkingInfo}
                </InfoRow>
                <InfoRow icon="ri-file-warning-line" label="アレルギー">
                  {STORE.allergyInfoUrl ? (
                    <a href={STORE.allergyInfoUrl} target="_blank" rel="noopener noreferrer" className="text-primary-700 underline cursor-pointer">
                      アレルギー情報を見る
                    </a>
                  ) : STORE.allergyNote ? (
                    STORE.allergyNote
                  ) : (
                    <Unconfirmed />
                  )}
                </InfoRow>
              </div>

              <div className="mt-6 pt-6 border-t border-background-300">
                <p className="font-heading font-bold text-foreground-950 text-sm mb-3 flex items-center gap-2">
                  <i className="ri-route-line text-primary-600" aria-hidden="true"></i>
                  アクセス
                </p>
                <ul className="space-y-1.5 text-xs text-foreground-700">
                  {STORE.access.map((a) => (
                    <li key={a.label} className="flex items-center gap-2">
                      <i
                        className={`${a.icon === "subway" ? "ri-subway-line" : "ri-train-line"} text-primary-500`}
                        aria-hidden="true"
                      ></i>
                      {a.label}
                    </li>
                  ))}
                </ul>
                {STORE.facilityUrl && (
                  <a
                    href={STORE.facilityUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs text-primary-700 underline underline-offset-2 cursor-pointer"
                  >
                    <i className="ri-external-link-line" aria-hidden="true"></i>
                    施設（{STORE.facilityName}）公式サイトを見る
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Right image + description */}
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="font-heading font-black text-xl md:text-2xl text-foreground-950 mb-2">
                神戸の人気スポット
                <br />
                <span className="text-primary-600">「umie」</span>でお待ちしています！
              </h3>
              <p className="text-sm text-foreground-700 leading-relaxed">
                海を感じるロケーションで、買い物やお出かけの合間に気軽に立ち寄れるフードコートで営業しています。
              </p>
            </div>

            <div className="rounded-xl overflow-hidden aspect-[4/3] bg-background-100">
              <img
                src={storeFacade}
                alt="店舗イメージ（メニューボード・カウンター）"
                width={800}
                height={600}
                loading="lazy"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Google Map embed */}
            <div className="rounded-xl overflow-hidden aspect-[16/9] border border-background-200">
              <iframe
                title={`${STORE.facilityName}地図`}
                src={STORE.googleMapsEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
